import { useState, useEffect } from 'react'
import Input from 'components/atoms/Input'
import Switch from 'components/atoms/Switch'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'
import { trim } from 'lodash'

import { useTranslation } from 'react-i18next'

const AddDriveWheelModal = ({ onSuccess, close, id = null, close: customClose }) => {
  const { t } = useTranslation()

  const [formData, setFormData] = useState({
    driveWheel: '',
    driveWheelAr: '',
    active: true,
  })

  useEffect(() => {
    if (id) {
      fetchDriveWheelData()
    }
  }, [id])

  const fetchDriveWheelData = async () => {
    try {
      const response = await apiGet(`drive-wheel/${id}`)
      if (response.success) {
        const data = response.data
        setFormData({
          driveWheel: data.driveWheel || '',
          driveWheelAr: data.driveWheelAr || '',
          active: data.active !== undefined ? data.active : true,
        })
      } else {
        notify.error(t('addDriveWheelModal.messages.failedToFetchDriveWheelData'))
      }
    } catch (error) {
      console.error('Error fetching drive wheel data:', error)
      notify.error(t('addDriveWheelModal.messages.failedToFetchDriveWheelData'))
    }
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleSwitchChange = checked => {
    setFormData(prevData => ({
      ...prevData,
      active: checked,
    }))
  }

  const handleSubmit = async () => {
    if (!trim(formData.driveWheel)) {
      notify.error(t('addDriveWheelModal.messages.driveWheelNameRequired'))
      return
    }
    if (!trim(formData.driveWheelAr)) {
      notify.error(t('addDriveWheelModal.messages.driveWheelArabicNameRequired'))
      return
    }

    try {
      let response
      if (id) {
        response = await apiPut(`drive-wheel/${id}`, formData)
        if (response.success) {
          notify.success(t('addDriveWheelModal.messages.driveWheelUpdatedSuccessfully'))
        }
      } else {
        response = await apiPost('drive-wheel', formData)
        if (response.success) {
          notify.success(t('addDriveWheelModal.messages.driveWheelAddedSuccessfully'))
        }
      }

      if (response.success) {
        onSuccess()
        if (customClose) customClose()
        close()
      } else {
        notify.error(response.message || t('addDriveWheelModal.messages.operationFailed'))
      }
    } catch (error) {
      console.error('Error saving drive wheel:', error)
      notify.error(t('addDriveWheelModal.messages.failedToSaveDriveWheel'))
    }
  }

  const handleCancel = () => {
    if (customClose) customClose()
    close()
  }

  return (
    <div className="space-y-6">
      <div>
        <label htmlFor="driveWheel" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addDriveWheelModal.labels.driveWheel')}
        </label>
        <input
          type="text"
          name="driveWheel"
          value={formData.driveWheel}
          onChange={handleChange}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder={t('addDriveWheelModal.placeholders.enterDriveWheel')}
        />
      </div>

      <div>
        <label htmlFor="driveWheelAr" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addDriveWheelModal.labels.driveArabicWheel')}
        </label>
        <input
          type="text"
          name="driveWheelAr"
          value={formData.driveWheelAr}
          onChange={handleChange}
          dir="rtl"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder={t('addDriveWheelModal.placeholders.enterDriveWheelArabic')}
        />
      </div>

      <div className="flex items-center justify-between">
        <label htmlFor="active" className="block text-sm font-medium text-gray-700">
          {t('addDriveWheelModal.labels.active')}
        </label>
        <Switch value={formData.active} onChange={handleSwitchChange} />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          onClick={handleCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {t('addDriveWheelModal.buttons.cancel')}
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
        >
          {id ? t('addDriveWheelModal.buttons.updateDriveWheel') : t('addDriveWheelModal.buttons.addDriveWheel')}
        </button>
      </div>
    </div>
  )
}

export default AddDriveWheelModal
